/**
 * NotificationBellTrigger — bell icon + unread badge inside <TopNav>.
 *
 * Owns the single `useNotificationStream()` call and prop-drills `streamState`
 * to `<NotificationsInbox>` (single-subscription-owner invariant). Wraps the
 * inbox in `<ConvexErrorBoundary>`.
 *
 * Badge rules (FR-007):
 * - Hidden when `unreadCount === 0`
 * - Shows the literal number when 1-99
 * - Shows "99+" when above 99
 *
 * Accessibility:
 * - `aria-label="Notifications, N unread"` on the trigger button
 * - Badge wrapped in `aria-live="polite"`
 * - 3-pixel `:focus-visible` focus ring (via CSS class `.notif-bell`)
 *
 * @see specs/prd.md Section 3 FR-007
 */
import React, { useCallback, useRef, useState, type CSSProperties } from "react";
import { useNotificationStream } from "../../api/hooks";
import { ConvexErrorBoundary } from "./ConvexErrorBoundary";
import { NotificationsInbox } from "./NotificationsInbox";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const bellButtonStyle: CSSProperties = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "36px",
  height: "36px",
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  borderRadius: "6px",
  color: "inherit",
};

const badgeStyle: CSSProperties = {
  position: "absolute",
  top: "2px",
  right: "2px",
  minWidth: "16px",
  height: "16px",
  padding: "0 4px",
  borderRadius: "8px",
  backgroundColor: "var(--color-sora, #3b82f6)",
  color: "#fff",
  fontSize: "10px",
  fontWeight: 600,
  lineHeight: "16px",
  textAlign: "center",
  pointerEvents: "none",
};

const popoverContainerStyle: CSSProperties = {
  position: "relative",
};

// ---------------------------------------------------------------------------
// Bell SVG icon (inline to avoid external dependency)
// ---------------------------------------------------------------------------

function BellIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Bell trigger component. Renders the bell icon with unread badge and
 * manages the popover open/close state.
 */
export function NotificationBellTrigger(): React.ReactElement {
  const streamState = useNotificationStream();
  const [isOpen, setIsOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);

  // -- Derive unread count from stream state ---------------------------------
  const unreadCount =
    streamState.status === "ready" ? streamState.data.unreadCount : 0;

  // -- Badge text per FR-007 rules -------------------------------------------
  const badgeText =
    unreadCount === 0 ? null : unreadCount > 99 ? "99+" : String(unreadCount);

  // -- Aria label with count -------------------------------------------------
  const ariaLabel = `Notifications, ${unreadCount} unread`;

  // -- Toggle popover --------------------------------------------------------
  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // -- Close popover (called by child or Escape) -----------------------------
  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Restore focus to the bell button on close (FR-008 accessibility)
    bellRef.current?.focus();
  }, []);

  return (
    <div style={popoverContainerStyle}>
      <button
        ref={bellRef}
        type="button"
        className="notif-bell"
        onClick={handleToggle}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        data-testid="notification-bell"
        style={bellButtonStyle}
      >
        <BellIcon />
        {badgeText != null && (
          <span
            aria-live="polite"
            data-testid="notification-badge"
            style={badgeStyle}
          >
            {badgeText}
          </span>
        )}
      </button>

      {isOpen && (
        <ConvexErrorBoundary>
          <NotificationsInbox
            streamState={streamState}
            onClose={handleClose}
          />
        </ConvexErrorBoundary>
      )}
    </div>
  );
}
